import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, Camera, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_MB = 5;

// Resize + compress via canvas so uploads stay under our 5MB base64 cap
const readAndCompress = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const maxDim = 1800;
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
        const base64 = dataUrl.split(",")[1];
        resolve({ base64, mime_type: "image/jpeg" });
      };
      img.onerror = () => reject(new Error("image load failed"));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const CookPhotos = ({ recipeId }) => {
  const { user } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  const fetchPhotos = () => {
    api.get(`/recipes/${recipeId}/cook_photos`)
      .then((res) => setPhotos(res.data?.photos || []))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Photo must be under ${MAX_MB}MB`);
      return;
    }
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreview(null);
    setCaption("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const submit = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const { base64, mime_type } = await readAndCompress(selectedFile);
      await api.post(`/recipes/${recipeId}/cook_photos`, {
        image_data: base64,
        mime_type,
        caption: caption.trim() || null,
      });
      toast.success("Photo added — nice plating");
      clearFile();
      fetchPhotos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (photoId) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(`/cook_photos/${photoId}`);
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <section className="border-t border-stone-800 pt-10 mt-16" data-testid="cook-photos">
      <div className="flex items-center gap-3 mb-2">
        <Camera className="w-4 h-4 text-orange-500" />
        <span className="eyebrow text-orange-500">Cooks&apos; gallery</span>
      </div>
      <h2 className="font-serif-display text-4xl md:text-5xl text-stone-100 leading-[0.95] tracking-tighter">
        Show us <span className="italic text-orange-600">yours</span>.
      </h2>
      <p className="mt-4 max-w-lg text-sm text-stone-400 leading-relaxed">
        Cooked this dish? Snap a photo, share it here. No filter, no fuss —
        the good, the wonky, the accidentally set-fire-to.
      </p>

      {/* Upload form */}
      <div className="mt-8">
        {user ? (
          <div className="border border-stone-800 bg-stone-900/40 p-6">
            {!preview ? (
              <label
                htmlFor={`cook-photo-input-${recipeId}`}
                className="cursor-pointer inline-flex items-center gap-3 bg-orange-700 hover:bg-orange-800 text-stone-50 px-6 py-3 uppercase text-xs tracking-[0.24em] transition-colors"
                data-testid="cook-photo-choose"
              >
                <Camera className="w-4 h-4" />
                Upload your photo
              </label>
            ) : (
              <div className="flex flex-col md:flex-row gap-6">
                <div className="relative shrink-0">
                  <img
                    src={preview}
                    alt="preview"
                    className="w-full md:w-56 h-56 object-cover"
                  />
                  <button
                    type="button"
                    onClick={clearFile}
                    aria-label="Clear"
                    className="absolute top-2 right-2 bg-stone-950/80 text-stone-200 p-1.5 hover:text-orange-500"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex-1">
                  <label className="block text-[10px] uppercase tracking-[0.24em] text-stone-500 mb-2">
                    Caption (optional)
                  </label>
                  <textarea
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    maxLength={280}
                    rows={3}
                    placeholder="How it went. What you tweaked. Whether the dog got the crackling."
                    data-testid="cook-photo-caption"
                    className="w-full bg-stone-950 border border-stone-800 focus:border-orange-600 text-stone-100 px-4 py-3 outline-none text-sm resize-none"
                  />
                  <div className="mt-4 flex items-center gap-4">
                    <button
                      type="button"
                      onClick={submit}
                      disabled={uploading}
                      data-testid="cook-photo-submit"
                      className="inline-flex items-center gap-3 bg-orange-700 hover:bg-orange-800 disabled:opacity-60 text-stone-50 px-6 py-3 uppercase text-xs tracking-[0.24em] transition-colors"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Uploading
                        </>
                      ) : (
                        <>Add photo</>
                      )}
                    </button>
                    <span className="text-[10px] uppercase tracking-[0.24em] text-stone-500">
                      Live immediately. Be kind.
                    </span>
                  </div>
                </div>
              </div>
            )}
            <input
              ref={inputRef}
              id={`cook-photo-input-${recipeId}`}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFile}
              className="hidden"
              data-testid="cook-photo-input"
            />
          </div>
        ) : (
          <div className="border border-stone-800 bg-stone-900/40 p-6">
            <p className="text-sm text-stone-400">
              Sign in to share your photo of this dish.
            </p>
          </div>
        )}
      </div>

      {/* Gallery */}
      <div className="mt-10">
        {loading ? (
          <div className="flex items-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-orange-600" />
          </div>
        ) : photos.length === 0 ? (
          <p className="text-sm text-stone-500 font-serif-display italic">
            No one&apos;s cooked this yet — be the first.
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="cook-photo-gallery">
            {photos.map((p) => (
              <div
                key={p.id}
                data-testid={`cook-photo-tile-${p.id}`}
                className="relative group border border-stone-800 bg-stone-900/30"
              >
                <div className="aspect-square bg-stone-900">
                  <img
                    src={`${BACKEND}/api/cook_photos/${p.id}/image`}
                    alt={p.caption || "cook photo"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-3 space-y-1">
                  {p.caption && (
                    <p className="text-xs text-stone-300 leading-snug italic line-clamp-3">
                      &ldquo;{p.caption}&rdquo;
                    </p>
                  )}
                  <p className="text-[9px] uppercase tracking-[0.24em] text-stone-500">
                    {p.user_display_name || "anonymous"}
                  </p>
                </div>
                {user && (user.id === p.user_id || user.is_admin) && (
                  <button
                    type="button"
                    onClick={() => remove(p.id)}
                    aria-label="Remove"
                    data-testid={`cook-photo-delete-${p.id}`}
                    className="absolute top-2 right-2 bg-stone-950/80 hover:bg-orange-700 text-stone-200 hover:text-stone-50 p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default CookPhotos;
